
import {occlusion} from '../../definitions/gl/sdf/occlusion.js';
import {MAX_DISTANCE} from '../../definitions/gl/sdf/raymarching.js';
import {gl_uniform, gl_variable} from '../../definitions/gl/variables.js';
import {gl_vec3} from '../../definitions/gl/vec.js';
import {gl_between} from '../../definitions/gl/sdf/sdf.js';
import {gl_times} from '../../definitions/gl/index.js';
import CustomZObject from '../CustomZObject.js';
import {sdf_soft_shadow} from '../../definitions/gl/sdf/shadows.js';
import {
    gl_diffused_light,
    estimateNormal,
} from '../../definitions/gl/sdf/normals.js';
import {
    gl_raymarching_3D}
from '../../definitions/gl/sdf/raymarching.js';

export default class RayMarchingManager extends CustomZObject {
    constructor(zObject) {
        super(zObject);
        zObject.skipHotReload = true;
        this.skipHotReload = true;

        this.setup();
        this.newInlet("GL SDF Evaluators", ([msg]) => this.onSDFs(msg));
        this.newInlet("Light Position", ([msg]) => this.onLightPosition(msg));
        this.newInlet("Light Diffusion", ([msg]) => this.onLightDiffusion(msg));
        this.newInlet("Camera Position", ([msg]) => this.onCameraPosition(msg));
        this.newInlet("Field of View", ([msg]) => this.onFieldOfView(msg));

        this.newOutlet("Light Strength");
        this.newOutlet("Raw Light Strength");
        this.newOutlet("Ray-Marched Distance");
        this.newOutlet("Ray-Marched Index");
        this.newOutlet("Normal");


        // The Data outlet will contain the scene, ray origin, ray direction, and ray marched distance
        // all together in one object, so that a light can use that
        this.newOutlet("Data");
    }

    hotReload() {
        if (this.lastSDFs) {
            this.onSDFs(this.lastSDFs);
        }
    }

    onCameraPosition(position) {
        this._cameraPosition = position;
        this.rayOriginXUniform([position[0]]);
        this.rayOriginYUniform([position[1]]);
        this.rayOriginZUniform([position[2]]);
    }

    onLightDiffusion(diff) {
        this._lightDiffusion = diff;
        this.lightDiffusionUniform([diff]);
    }

    onLightPosition(position) {
        if (!Array.isArray(position)) {
            return;
        }
        this._lightPosition = position;

        this.lightPositionXUniform([position[0]]);
        this.lightPositionYUniform([position[1]]);
        this.lightPositionZUniform([position[2]]);
    }

    setup() {
        this.rayOriginXUniform = gl_uniform({}, () => this.name('rayOriginX'));
        this.rayOriginYUniform = gl_uniform({}, () => this.name('rayOriginY'));
        this.rayOriginZUniform = gl_uniform({}, () => this.name('rayOriginZ'));
        this.rayOriginVec3 = gl_vec3(
            this.zObject,
            () => this.rayOriginYUniform([this.rayOriginY], true),
            () => this.rayOriginZUniform([this.rayOriginZ], true));

        this.fieldOfViewUniform = gl_uniform({}, () => this.name('fieldOfView'));


        this.lightPositionXUniform = gl_uniform({}, () => this.name('lighPositionX'));
        this.lightPositionYUniform = gl_uniform({}, () => this.name('lightPositionY'));
        this.lightPositionZUniform = gl_uniform({}, () => this.name('lightPositionZ'));

        this.lightDiffusionUniform = gl_uniform({}, () => this.name('lightDiffusion'));

        this.lightPositionVec3 = gl_vec3(
            this.zObject,
            () => this.lightPositionYUniform([this.lightPositionY], true),
            () => this.lightPositionZUniform([this.lightPositionZ], true));
        
        // default
        this.raymarcher = gl_raymarching_3D(
            this.zObject,
            () => this.rayOrigin,
            () => this.fieldOfViewUniform([this.fieldOfView], true),
            () => this.lightPosition);

        this.normalEstimator = estimateNormal(
            this.zObject,
            () => this.rayOrigin,
            () => this.rayDirection,
            () => this.rayMarchedDistance);

        this.lightDiffuser = gl_diffused_light(
            this.zObject,
            () => this.rayOrigin,
            () => this.rayDirection,
            () => this.rayMarchedDistance,
            () => this.lightPosition,
            () => this.lightDiffusionUniform([this.lightDiffusion], true),
            () => 0.64
        );

        this.occlusion = occlusion(
            this.zObject,
            () => this.rayOrigin,
            () => this.rayDirection,
            () => this.rayMarchedDistance,
            () => this.normal,
        );
    }

    onFieldOfView(fieldOfView) {
        this.fieldOfViewUniform([fieldOfView]);
    }

    get fieldOfView() {
        return 90;
    }

    get lightDiffusion() {
        return this._lightDiffusion === undefined ? 1.96 : this._lightDiffusion;
    }

    get rayOriginX() {
        return this._cameraPosition ?
            this._cameraPosition[0] : 0.2;
    }

    get rayOriginY() {
        return this._cameraPosition ?
            this._cameraPosition[1] : 0.2;
    }

    get rayOriginZ() {
        return this._cameraPosition ?
            this._cameraPosition[2] : 1;
    }

    get lightPositionX() {
        return this._lightPosition ?
            this._lightPosition[0] : 0;
    }

    get lightPositionY() {
        return this._lightPosition ?
            this._lightPosition[1] : 1.8;
    }

    get lightPositionZ() {
        return this._lightPosition ?
            this._lightPosition[2] : 1.8;
    }

    get rayOrigin() {
        let x = this.rayOriginXUniform([this.rayOriginX], true);
        return this.rayOriginVec3([x]);
    }

    get lightPosition() {
        return this.lightPositionVec3([this.lightPositionXUniform([this.lightPositionX], true)]);
    }

    // Takes in a scene generated from SDFManager and applies
    // raymarching to the scene given the light position/camera position etc
    onSDFs(scene) {
        // force update of uniforms
        this.rayOriginXUniform([this.rayOriginX]);
        this.rayOriginYUniform([this.rayOriginY]);

        this.lastSDFs = scene;

        let l = this.raymarcher([scene]);
        let [rayMarchedDistance, rayDirection] = l;
        let rmName = this.name("rayMarchedReturn");
        let dist = {
            ... rayMarchedDistance,
            code: rmName + '.x'
        };
        let distIndex = {
            ... rayMarchedDistance,
            code: rmName + '.y'
        };
        this.rayMarchedReturn = gl_variable(this.zObject, () => rmName, () => 'vec4')([rayMarchedDistance]);
        this.rayMarchedDistance = gl_variable(this.zObject, () => this.name('rayMarchedDistance'), () => 'float')([dist]);
        this.rayMarchedIndex = gl_variable(this.zObject, () => this.name('rayMarchedIndex'), () => 'float')([distIndex]);
        this.rayDirection = gl_variable(this.zObject, () => this.name('rayDirection'), () => 'vec3')([rayDirection]);

        let estimator = this.normalEstimator([scene]);
        let estimated = estimator ; 
        let diffused = this.lightDiffuser([estimated]);
        let between = gl_between(this.zObject, () => 0, () => MAX_DISTANCE);
        this.normal = estimated;

        let mult = gl_times(this.zObject, () => between([this.rayMarchedDistance]));

        let shadow = gl_variable(this.zObject, () => this.name("shadow"), () => 'float')([
            {
                ...this.rayMarchedReturn,
                code: this.rayMarchedReturn.code + '.z'
        }]);

        let hitNormal = gl_variable(this.zObject, () => this.name("hitNormal"), () => 'float')([
            {
                ...this.rayMarchedReturn,
                code: this.rayMarchedReturn.code + '.w'
        }]);

        let occlusionRet = this.occlusion([this.lastSDFs]);
        let occlusionVariable = gl_variable(this.zObject, () => this.name("occlusion"), () => 'float')([
            occlusionRet,
        ]);

        // let shadowPlay = gl_times(this.zObject, () => diffused);
        let ret = mult([diffused]);

        let functions = scene.functions;
        ret.functions = {
            ... ret.functions,
            ... estimator.functions,
            ... rayMarchedDistance.functions,
            ... occlusionRet.functions,
            ... functions,
        };
        ret.variables = {
            ... shadow.variables,
            ... hitNormal.variables,
            ... this.rayDirection.variables,
            ... this.rayMarchedReturn.variables,
            ... this.rayMarchedDistance.variables,
            ... this.rayMarchedIndex.variables,
            ... occlusionVariable.variables,
            ... ret.variables,
            ... estimated.variables,
        };

        this.rayMarchedIndex.variables = {
            ... shadow.variables,
            ... hitNormal.variables,
            ... this.rayDirection.variables,
            ... this.rayMarchedReturn.variables,
            ... this.rayMarchedDistance.variables,
            ... this.rayMarchedIndex.variables,
            ... occlusionVariable.variables,
            ... ret.variables,
            ... estimated.variables,
        };

        this.rayMarchedDistance.functions = {
            ... this.rayMarchedDistance.functions,
            ... occlusionRet.functions,
        };

        this.rayMarchedDistance.variables = {
            ... shadow.variables,
            ... hitNormal.variables,
            ... this.rayMarchedReturn.variables,
            ... this.rayMarchedIndex.variables,
            ... this.rayMarchedDistance.variables,
            ... occlusionVariable.variables,
        };
        this.sendMessage("Light Strength", ret);
        this.sendMessage("Ray-Marched Distance", this.rayMarchedDistance);
        this.sendMessage("Normal", estimated);
        this.sendMessage("Ray-Marched Index", this.rayMarchedIndex);

        // send all this data out so that light objects can use it to calculate
        // lighting
        this.sendMessage("Data", {
            occlusion: occlusionVariable,
            rayOrigin: this.rayOrigin,
            rayDirection: this.rayDirection,
            rayMarchedDistance: this.rayMarchedDistance,
            scene: this.lastSDFs,
            normal: estimated
        });
    }


    name(name) {
        let id = this.id.replaceAll('-', '');
        let y = name + id;
        return y;
    }
}

